import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Banknote, Loader2, RefreshCw, X, History, Plus, Pencil, Trash2, Check, AlertCircle, Search, UserPlus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiCall, type Member } from '@/lib/api';
import { wiqProfileUrl } from '@/lib/utils';

// Cash payments — status and expected dates come from the legacy cash DB the
// delinquency texts use; every change is written to both it and membersyncpro
// payments by the API (see apps/api/src/routes/cash.ts).

type BalanceUnit = 'days' | 'weeks' | 'months';
type CashState = 'overdue' | 'due-soon' | 'active' | 'no-payments';

interface CashRow {
  legacy_wrestler_id: number;
  member_id: string | null;
  name: string;
  wiq_id: number | null;
  expected_day_of_month: number | null;
  days_remaining: number | null;
  paid_through: string | null;
  next_due: string | null;
  last_payment: { date: string; amount: number; balance_amount: number; balance_unit: BalanceUnit } | null;
  payment_count: number;
  state: CashState;
}

interface CashPayment {
  id: number;
  date: string;
  amount: number;
  balance_amount: number;
  balance_unit: BalanceUnit;
  days_added: number;
  msp_payment_id: string | null;
}

const STATE: Record<CashState, { label: string; className: string }> = {
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  'due-soon': { label: 'Due soon', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  active: { label: 'Paid up', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  'no-payments': { label: 'No payments', className: 'bg-muted text-muted-foreground' },
};

const money = (n: number) => `$${n.toFixed(n % 1 ? 2 : 0)}`;
const fmtDate = (d: string | null) =>
  d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';
const todayPacific = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());

function stateDetail(r: CashRow) {
  if (r.days_remaining === null) return '';
  if (r.days_remaining < 0) return `${-r.days_remaining} day${r.days_remaining === -1 ? '' : 's'} overdue`;
  return `${r.days_remaining} day${r.days_remaining === 1 ? '' : 's'} left`;
}

export default function CashPayments() {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<CashRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<CashState | 'all'>('all');
  const [q, setQ] = useState('');
  const [paying, setPaying] = useState<CashRow | 'new' | null>(null);
  const [history, setHistory] = useState<CashRow | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const r = await apiCall<{ items: CashRow[] }>('/v1/cash', { bearerToken: token });
      setRows(r.items);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed to load'); }
    finally { setLoading(false); }
  }, [getToken]);
  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => rows.reduce<Record<string, number>>((a, r) => { a[r.state] = (a[r.state] ?? 0) + 1; return a; }, {}), [rows]);
  const shown = useMemo(() => rows
    .filter((r) => filter === 'all' || r.state === filter)
    .filter((r) => !q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase()))
    // Most urgent first: most overdue → soonest due → furthest out.
    .sort((a, b) => (a.days_remaining ?? 1e9) - (b.days_remaining ?? 1e9)), [rows, filter, q]);

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Banknote className="h-6 w-6" /> Cash payments</h1>
          <p className="text-sm text-muted-foreground">
            Members who pay cash, when they're paid through, and when the next payment is expected. Same numbers the daily texts use.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}><UserPlus className="mr-1 h-4 w-4" /> Add member</Button>
          <Button size="sm" onClick={() => setPaying('new')}><Plus className="mr-1 h-4 w-4" /> Take payment</Button>
        </div>
      </div>

      {err && <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {err}</CardContent></Card>}

      <div className="flex flex-wrap items-center gap-2">
        {([['all', 'All', rows.length], ['overdue', 'Overdue', counts.overdue ?? 0], ['due-soon', 'Due in 7 days', counts['due-soon'] ?? 0], ['active', 'Paid up', counts.active ?? 0], ['no-payments', 'No payments yet', counts['no-payments'] ?? 0]] as const).map(([key, label, n]) => (
          <button key={key} onClick={() => setFilter(key)}
            className={'rounded-full border px-3 py-1 text-xs transition-colors ' +
              (filter === key ? 'bg-foreground text-background border-foreground' : 'text-muted-foreground hover:text-foreground hover:border-foreground/40')}>
            {label} <span className="ml-1 opacity-70">{n}</span>
          </button>
        ))}
        <div className="ml-auto relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name"
            className="border rounded pl-7 pr-2 py-1.5 text-sm w-48 bg-background" />
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b">
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Paid through</th>
                <th className="px-3 py-2 font-medium">Next payment expected</th>
                <th className="px-3 py-2 font-medium">Last payment</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.legacy_wrestler_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-3 py-2">
                    {r.member_id
                      ? <Link to={`/dashboard/members/${r.member_id}`} className="font-medium hover:underline">{r.name}</Link>
                      : <span className="font-medium">{r.name}</span>}
                    {r.wiq_id && <a href={wiqProfileUrl(r.wiq_id)} target="_blank" rel="noreferrer" className="ml-2 text-xs text-muted-foreground hover:text-foreground hover:underline">WIQ ↗</a>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${STATE[r.state].className}`}>{STATE[r.state].label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{stateDetail(r)}</span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.paid_through)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {fmtDate(r.next_due)}
                    {r.expected_day_of_month && <div className="text-xs text-muted-foreground">usually the {r.expected_day_of_month}{ordinal(r.expected_day_of_month)}</div>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    {r.last_payment
                      ? <>{fmtDate(r.last_payment.date)} · {money(r.last_payment.amount)}<div className="text-muted-foreground">{r.last_payment.balance_amount} {r.last_payment.balance_unit} · {r.payment_count} total</div></>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setHistory(r)}><History className="mr-1 h-3.5 w-3.5" /> History</Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 ml-1" onClick={() => setPaying(r)}><Banknote className="mr-1 h-3.5 w-3.5" /> Take payment</Button>
                  </td>
                </tr>
              ))}
              {!loading && shown.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-muted-foreground">No cash members match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {paying && <TakePaymentDialog row={paying === 'new' ? null : paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); void load(); }} />}
      {adding && <AddMemberDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); void load(); }} />}
      {history && <HistoryDialog row={history} onClose={() => setHistory(null)} onChanged={() => void load()} />}
    </div>
  );
}

const ordinal = (n: number) => (n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th');

function PaymentFields({ v, set }: {
  v: { date: string; amount: string; balance_amount: string; balance_unit: BalanceUnit };
  set: (patch: Partial<{ date: string; amount: string; balance_amount: string; balance_unit: BalanceUnit }>) => void;
}) {
  const input = 'w-full border rounded px-2 py-1.5 text-sm bg-background';
  return (
    <div className="grid grid-cols-2 gap-3">
      <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Date paid</span>
        <input type="date" value={v.date} onChange={(e) => set({ date: e.target.value })} className={input} required /></label>
      <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Amount ($)</span>
        <input type="number" min="0" step="0.01" value={v.amount} onChange={(e) => set({ amount: e.target.value })} className={input} required /></label>
      <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Covers</span>
        <input type="number" min="1" max="366" value={v.balance_amount} onChange={(e) => set({ balance_amount: e.target.value })} className={input} required /></label>
      <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">&nbsp;</span>
        <select value={v.balance_unit} onChange={(e) => set({ balance_unit: e.target.value as BalanceUnit })} className={input}>
          <option value="days">days</option><option value="weeks">weeks</option><option value="months">months</option>
        </select></label>
    </div>
  );
}

function TakePaymentDialog({ row, onClose, onDone }: { row: CashRow | null; onClose: () => void; onDone: () => void }) {
  const { getToken } = useAuth();
  const last = row?.last_payment;
  const [v, setV] = useState({
    date: todayPacific(),
    amount: last ? String(last.amount) : '',
    balance_amount: last ? String(last.balance_amount) : '30',
    balance_unit: (last?.balance_unit ?? 'days') as BalanceUnit,
  });
  const [notes, setNotes] = useState('');
  const [memberQ, setMemberQ] = useState('');
  const [matches, setMatches] = useState<Member[]>([]);
  const [member, setMember] = useState<Member | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (row || memberQ.trim().length < 2) { setMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const token = await getToken();
        const r = await apiCall<{ items: Member[] }>('/v1/members', { bearerToken: token, query: { search: memberQ.trim(), per_page: 8 } });
        setMatches(r.items);
      } catch { /* ignore search errors */ }
    }, 250);
    return () => clearTimeout(t);
  }, [memberQ, row, getToken]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/cash/payments', {
        method: 'POST', bearerToken: token,
        body: {
          ...(row ? (row.member_id ? { member_id: row.member_id } : { legacy_wrestler_id: row.legacy_wrestler_id }) : { member_id: member?.id }),
          date: v.date, amount: Number(v.amount), balance_amount: Number(v.balance_amount), balance_unit: v.balance_unit,
          notes: notes.trim() || null,
        },
      });
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Payment failed'); }
    finally { setBusy(false); }
  }

  const who = row?.name ?? member?.name;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Take cash payment{who ? ` — ${who}` : ''}</h2>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>
            {!row && (
              <div>
                <input value={member ? member.name : memberQ} onChange={(e) => { setMember(null); setMemberQ(e.target.value); }}
                  placeholder="Search member" className="w-full border rounded px-2 py-1.5 text-sm bg-background" autoFocus />
                {!member && matches.length > 0 && (
                  <ul className="mt-1 border rounded divide-y max-h-48 overflow-y-auto">
                    {matches.map((m) => (
                      <li key={m.id}><button type="button" onClick={() => setMember(m)} className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted/50">{m.name}</button></li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <PaymentFields v={v} set={(patch) => setV((s) => ({ ...s, ...patch }))} />
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Note (optional)</span>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm bg-background" /></label>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={busy || (!row && !member) || !v.amount || !v.balance_amount}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record payment
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}

function HistoryDialog({ row, onClose, onChanged }: { row: CashRow; onClose: () => void; onChanged: () => void }) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<CashPayment[] | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState({ date: '', amount: '', balance_amount: '', balance_unit: 'days' as BalanceUnit });
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await apiCall<{ items: CashPayment[] }>(`/v1/cash/${row.legacy_wrestler_id}/payments`, { bearerToken: token });
      setItems(r.items);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed to load'); }
  }, [getToken, row.legacy_wrestler_id]);
  useEffect(() => { void load(); }, [load]);

  async function save(id: number) {
    setErr(null);
    try {
      const token = await getToken();
      await apiCall(`/v1/cash/payments/${id}`, { method: 'PATCH', bearerToken: token, body: {
        date: draft.date, amount: Number(draft.amount), balance_amount: Number(draft.balance_amount), balance_unit: draft.balance_unit,
      } });
      setEditing(null); await load(); onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Save failed'); }
  }

  async function remove(id: number) {
    setErr(null);
    try {
      const token = await getToken();
      await apiCall(`/v1/cash/payments/${id}`, { method: 'DELETE', bearerToken: token });
      setConfirmDelete(null); await load(); onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Delete failed'); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Payment history — {row.name}</h2>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          {!items ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payments recorded.</p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-2 font-medium">Date</th><th className="py-2 pr-2 font-medium">Amount</th>
                  <th className="py-2 pr-2 font-medium">Covers</th><th className="py-2 font-medium text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {items.map((p) => editing === p.id ? (
                    <tr key={p.id} className="border-b"><td colSpan={4} className="py-2">
                      <PaymentFields v={draft} set={(patch) => setDraft((s) => ({ ...s, ...patch }))} />
                      <div className="flex justify-end gap-2 mt-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button size="sm" onClick={() => void save(p.id)}><Check className="mr-1 h-3.5 w-3.5" /> Save</Button>
                      </div>
                    </td></tr>
                  ) : (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="py-2 pr-2">{fmtDate(p.date)}</td>
                      <td className="py-2 pr-2">{money(p.amount)}</td>
                      <td className="py-2 pr-2">{p.balance_amount} {p.balance_unit} <span className="text-xs text-muted-foreground">({p.days_added} days)</span></td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {confirmDelete === p.id ? (
                          <span className="inline-flex items-center gap-1 text-xs">Delete?
                            <Button variant="destructive" size="sm" className="h-7 px-2" onClick={() => void remove(p.id)}>Yes</Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setConfirmDelete(null)}>No</Button>
                          </span>
                        ) : (<>
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => {
                            setDraft({ date: p.date, amount: String(p.amount), balance_amount: String(p.balance_amount), balance_unit: p.balance_unit });
                            setEditing(p.id); setConfirmDelete(null);
                          }}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => { setConfirmDelete(p.id); setEditing(null); }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Put a member on the cash list without a payment yet (first payment comes
// later from their row). Cash tracking keys on the member's WrestlingIQ id.
function AddMemberDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { getToken } = useAuth();
  const [memberQ, setMemberQ] = useState('');
  const [matches, setMatches] = useState<Member[]>([]);
  const [member, setMember] = useState<Member | null>(null);
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (memberQ.trim().length < 2) { setMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const token = await getToken();
        const r = await apiCall<{ items: Member[] }>('/v1/members', { bearerToken: token, query: { search: memberQ.trim(), per_page: 8 } });
        setMatches(r.items);
      } catch { /* ignore search errors */ }
    }, 250);
    return () => clearTimeout(t);
  }, [memberQ, getToken]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!member) return;
    setBusy(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/cash/members', { method: 'POST', bearerToken: token, body: { member_id: member.id, expected_day_of_month: day ? Number(day) : null } });
      onDone();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not add';
      setErr(/already_on_cash_list/.test(msg) ? `${member.name} is already on the cash list.`
        : /member_has_no_wiq_id/.test(msg) ? `${member.name} has no WrestlingIQ id, which cash tracking needs.` : msg);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Add member to cash list</h2>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>
            <div>
              <input value={member ? member.name : memberQ} onChange={(e) => { setMember(null); setMemberQ(e.target.value); }}
                placeholder="Search member" className="w-full border rounded px-2 py-1.5 text-sm bg-background" autoFocus />
              {!member && matches.length > 0 && (
                <ul className="mt-1 border rounded divide-y max-h-48 overflow-y-auto">
                  {matches.map((m) => (
                    <li key={m.id}><button type="button" onClick={() => setMember(m)} className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted/50">{m.name}</button></li>
                  ))}
                </ul>
              )}
            </div>
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Usual payment day of the month (optional)</span>
              <input type="number" min="1" max="31" value={day} onChange={(e) => setDay(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm bg-background" /></label>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={!member || busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add to cash list</Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
